import React, { useState, useEffect } from "react";
import { getVideos, getChannels, getEditors, getShorts, createShort, updateShort, deleteShort } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { FilmStrip, Plus, Pencil, Trash, CalendarBlank, YoutubeLogo, TiktokLogo } from "@phosphor-icons/react";
import { Facebook } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const STAGES = ["idea", "script", "filming", "editing", "review", "ready", "scheduled", "published"];

export default function Shorts() {
  const { user } = useAuth();
  const canEdit = ["owner", "admin", "manager"].includes(user?.role);

  const [shorts, setShorts] = useState([]);
  const [videos, setVideos] = useState([]);
  const [channels, setChannels] = useState([]);
  const [editors, setEditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterChannel, setFilterChannel] = useState("");
  const [filterStage, setFilterStage] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingShort, setEditingShort] = useState(null);
  const [deletingShort, setDeletingShort] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    video_id: "",
    channel_id: "",
    num_shorts: 1,
    stage: "idea",
    editor_id: "",
    scheduled_date: "",
    notes: "",
  });

  const fetchData = async () => {
    try {
      const [shortsRes, videosRes, channelsRes, editorsRes] = await Promise.all([
        getShorts(),
        getVideos(),
        getChannels(),
        getEditors(),
      ]);
      setShorts(shortsRes.data);
      setVideos(videosRes.data);
      setChannels(channelsRes.data);
      setEditors(editorsRes.data);
    } catch (err) {
      toast.error("Failed to load shorts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredShorts = shorts.filter((s) => {
    if (filterChannel && s.channel_id !== filterChannel) return false;
    if (filterStage && s.stage !== filterStage) return false;
    return true;
  });

  const stats = {
    total: shorts.length,
    ready: shorts.filter((s) => ["ready", "scheduled"].includes(s.stage)).length,
    published: shorts.filter((s) => s.stage === "published").length,
    noDate: shorts.filter((s) => !s.scheduled_date && s.stage !== "published").length,
  };

  const handleOpenCreate = () => {
    setEditingShort(null);
    setFormData({
      title: "",
      video_id: "",
      channel_id: "",
      num_shorts: 1,
      stage: "idea",
      editor_id: "",
      scheduled_date: "",
      notes: "",
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (short) => {
    setEditingShort(short);
    setFormData({
      title: short.title,
      video_id: short.video_id || "",
      channel_id: short.channel_id || "",
      num_shorts: 1,
      stage: short.stage,
      editor_id: short.editor_id || "",
      scheduled_date: short.scheduled_date || "",
      notes: short.notes || "",
    });
    setDialogOpen(true);
  };

  const handleVideoSelect = (videoId) => {
    const video = videos.find(v => v.id === videoId);
    setFormData(prev => ({
      ...prev,
      video_id: videoId,
      channel_id: video?.channel_id || prev.channel_id,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingShort) {
        await updateShort(editingShort.id, {
          title: formData.title,
          stage: formData.stage,
          editor_id: formData.editor_id || null,
          scheduled_date: formData.scheduled_date || null,
          notes: formData.notes,
        });
        toast.success("Short updated");
      } else {
        const count = Math.max(1, formData.num_shorts);
        for (let i = 0; i < count; i++) {
          const title = count > 1 ? `${formData.title} #${i + 1}` : formData.title;
          await createShort({
            title,
            video_id: formData.video_id,
            channel_id: formData.channel_id,
            stage: formData.stage,
            editor_id: formData.editor_id || null,
            scheduled_date: formData.scheduled_date || null,
            notes: formData.notes,
          });
        }
        toast.success(`${count} short${count > 1 ? "s" : ""} created`);
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async () => {
    if (!deletingShort) return;
    try {
      await deleteShort(deletingShort.id);
      toast.success("Short deleted");
      setDeletingShort(null);
      fetchData();
    } catch (err) {
      toast.error("Failed to delete short");
    }
  };

  const handleInlineStageUpdate = async (shortId, newStage) => {
    try {
      await updateShort(shortId, { stage: newStage });
      setShorts(prev => prev.map(s => s.id === shortId ? { ...s, stage: newStage } : s));
      toast.success("Stage updated");
    } catch (err) {
      toast.error("Failed to update stage");
    }
  };

  const handleInlinePlatformUpdate = async (shortId, field, value) => {
    try {
      await updateShort(shortId, { [field]: value });
      setShorts(prev => prev.map(s => s.id === shortId ? { ...s, [field]: value } : s));
      toast.success("Updated");
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="surface-card p-4"><div className="skeleton h-12 w-full" /></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="stat-card">
          <p className="text-sm text-gray-400">Total Shorts</p>
          <p className="text-3xl font-heading font-semibold text-white">{stats.total}</p>
        </div>
        <div className="stat-card">
          <p className="text-sm text-gray-400">Ready to Publish</p>
          <p className="text-3xl font-heading font-semibold text-green-400">{stats.ready}</p>
        </div>
        <div className="stat-card">
          <p className="text-sm text-gray-400">Published</p>
          <p className="text-3xl font-heading font-semibold text-blue-400">{stats.published}</p>
        </div>
        <div className="stat-card">
          <p className="text-sm text-gray-400">No Schedule</p>
          <p className="text-3xl font-heading font-semibold text-amber-400">{stats.noDate}</p>
        </div>
      </div>

      {/* Filters + Create */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Select value={filterChannel} onValueChange={(v) => setFilterChannel(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48 bg-white/5 border-white/10 text-white" data-testid="shorts-filter-channel">
              <SelectValue placeholder="All Channels" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A2E] border-white/10">
              <SelectItem value="all" className="text-white">All Channels</SelectItem>
              {channels.map((c) => (
                <SelectItem key={c.id} value={c.id} className="text-white">{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterStage} onValueChange={(v) => setFilterStage(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white" data-testid="shorts-filter-stage">
              <SelectValue placeholder="All Stages" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A2E] border-white/10">
              <SelectItem value="all" className="text-white">All Stages</SelectItem>
              {STAGES.map((s) => (
                <SelectItem key={s} value={s} className="text-white capitalize">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <span className="text-sm text-gray-400">{filteredShorts.length} shorts</span>
        </div>

        {canEdit && (
          <Button onClick={handleOpenCreate} className="btn-primary gap-2" data-testid="create-short-btn">
            <Plus size={16} weight="bold" />
            Create Short
          </Button>
        )}
      </div>

      {/* Shorts Table */}
      <div className="surface-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#0B0C10] text-[#A0AEC0] text-sm uppercase tracking-wider">
                <th className="text-left p-4 font-medium">Title</th>
                <th className="text-left p-4 font-medium">Parent Video</th>
                <th className="text-left p-4 font-medium">Channel</th>
                <th className="text-left p-4 font-medium">Stage</th>
                <th className="text-left p-4 font-medium">Platforms</th>
                <th className="text-left p-4 font-medium">Editor</th>
                <th className="text-left p-4 font-medium">Scheduled</th>
                <th className="text-left p-4 font-medium">Published</th>
                {canEdit && <th className="text-left p-4 font-medium">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredShorts.map((short, idx) => (
                <tr key={short.id} className={`table-row ${idx % 2 === 1 ? "table-row-even" : ""}`} data-testid={`short-row-${short.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <FilmStrip size={16} className="text-purple-400" />
                      <span className="text-white font-medium">{short.title}</span>
                    </div>
                  </td>
                  <td className="p-4 text-gray-400 text-sm">{short.video_title || "-"}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: short.channel_color || "#3B82F6" }} />
                      <span className="text-gray-300">{short.channel_name || "-"}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    {canEdit ? (
                      <Select value={short.stage} onValueChange={(v) => handleInlineStageUpdate(short.id, v)}>
                        <SelectTrigger className={`w-28 h-7 text-xs badge-${short.stage} border-0 bg-transparent`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A2E] border-white/10">
                          {STAGES.map((s) => (
                            <SelectItem key={s} value={s} className="text-white capitalize">{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className={`badge badge-${short.stage}`}>{short.stage}</span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1" data-testid={`short-platforms-${short.id}`}>
                      {[
                        { key: "uploaded_youtube", icon: <YoutubeLogo size={13} weight="fill" className="text-red-500" />, label: "YouTube" },
                        { key: "uploaded_facebook", icon: <Facebook size={13} className="text-blue-500" />, label: "Facebook" },
                        { key: "uploaded_tiktok", icon: <TiktokLogo size={13} weight="fill" />, label: "TikTok" },
                      ].map((p) => (
                        <button
                          key={p.key}
                          type="button"
                          onClick={canEdit ? () => handleInlinePlatformUpdate(short.id, p.key, !short[p.key]) : undefined}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                            short[p.key]
                              ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                              : "bg-white/5 text-gray-500 border border-white/10"
                          } ${canEdit ? "cursor-pointer hover:bg-white/10" : "cursor-default"}`}
                          title={`${p.label}: ${short[p.key] ? "Uploaded" : "Pending"}`}
                          data-testid={`short-platform-${p.label.toLowerCase()}-${short.id}`}
                        >
                          {p.icon}
                          <span>{short[p.key] ? "\u2705" : "\u23F3"}</span>
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="p-4 text-gray-400">{short.editor_name || "Unassigned"}</td>
                  <td className="p-4 text-gray-400">{short.scheduled_date || "-"}</td>
                  <td className="p-4 text-gray-400">{short.published_date || "-"}</td>
                  {canEdit && (
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleOpenEdit(short)}
                          className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-white"
                          data-testid={`edit-short-${short.id}`}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => setDeletingShort(short)}
                          className="p-1.5 rounded hover:bg-red-500/10 text-gray-400 hover:text-red-400"
                          data-testid={`delete-short-${short.id}`}
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredShorts.length === 0 && (
          <div className="text-center py-12">
            <FilmStrip size={48} className="text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No shorts found</p>
            {canEdit && (
              <Button onClick={handleOpenCreate} variant="outline" className="btn-secondary mt-4 gap-2">
                <Plus size={14} /> Create your first short
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Short Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#1A1A2E] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingShort ? "Edit Short" : "Create Short"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Short title"
                required
                data-testid="short-title-input"
              />
            </div>

            {!editingShort && (
              <>
                <div className="space-y-2">
                  <Label className="text-gray-300">Parent Video</Label>
                  <Select value={formData.video_id} onValueChange={handleVideoSelect}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="short-video-select">
                      <SelectValue placeholder="Select parent video" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A2E] border-white/10 max-h-60">
                      {videos.map((v) => (
                        <SelectItem key={v.id} value={v.id} className="text-white">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: v.channel_color || "#3B82F6" }} />
                            <span className="truncate">{v.title}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Number of Shorts</Label>
                  <Input
                    type="number"
                    min="1"
                    max="20"
                    value={formData.num_shorts}
                    onChange={(e) => setFormData({ ...formData, num_shorts: parseInt(e.target.value) || 1 })}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="short-count-input"
                  />
                  {formData.num_shorts > 1 && (
                    <p className="text-xs text-gray-500">Will create {formData.num_shorts} shorts numbered #1 to #{formData.num_shorts}</p>
                  )}
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Stage</Label>
                <Select value={formData.stage} onValueChange={(v) => setFormData({ ...formData, stage: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A2E] border-white/10">
                    {STAGES.map((s) => (
                      <SelectItem key={s} value={s} className="text-white capitalize">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Editor</Label>
                <Select value={formData.editor_id || "none"} onValueChange={(v) => setFormData({ ...formData, editor_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Select editor" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A2E] border-white/10">
                    <SelectItem value="none" className="text-gray-400">Unassigned</SelectItem>
                    {editors.map((e) => (
                      <SelectItem key={e.id} value={e.id} className="text-white">{e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Scheduled Date</Label>
              <Input
                type="date"
                value={formData.scheduled_date}
                onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                data-testid="short-scheduled-date"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="bg-white/5 border-white/10 text-white min-h-[60px]"
                placeholder="Notes..."
                data-testid="short-notes-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 btn-secondary">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 btn-primary" data-testid="short-submit-btn">
                {editingShort ? "Save Changes" : `Create ${formData.num_shorts > 1 ? formData.num_shorts + " Shorts" : "Short"}`}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingShort} onOpenChange={() => setDeletingShort(null)}>
        <AlertDialogContent className="bg-[#1A1A2E] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Short</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Are you sure you want to delete "{deletingShort?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
